import React, { Component } from 'react'
import * as R from 'ramda'
import { BrowserRouter as Router, Redirect, Route, withRouter } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { CrashTest, ErrorFallback, reportRenderError } from './ErrorFallback.js'
import Login from './UserLogin.js'
import Player from './Player.js'
import './App.css'
import Settings from './Settings.js'
import Spinner from './Spinner.js'
import Admin from './Admin.js'
import AdminDuplicates from './AdminDuplicates.js'
import AdminMislabeled from './AdminMislabeled.js'
import AdminArtistSplit from './AdminArtistSplit.js'
import AdminJobs from './AdminJobs.js'
import { launchMessages } from './launchMessages'

import { requestJSONwithCredentials, requestWithCredentials } from './request-json-with-credentials.js'
import config from './config.js'

import 'typeface-lato'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Onboarding from './Onboarding'
import TopBar from './TopBar'
import { trackArtistsAndTitleText } from './trackFunctions'
import FollowPopup from './FollowPopup'
import IgnorePopup from './IgnorePopup'
import KeyboardShortcutsPopup from './KeyboardShortcutsPopup'

import { isMobile } from 'react-device-detect'
import { events, subscribe, unsubscribe } from './events'
import {
  parseSearchTerms,
  searchTermsToString,
  searchTermsToQueryString,
  addEntityTerm,
  entityNamesToUrlParam,
  applyEntityNamesFromUrlParam,
  findCartSearchTerm,
} from './searchTerms'

library.add(fas, far, fab)

// import injectTapEventPlugin from 'react-tap-event-plugin';
// injectTapEventPlugin();

const logoutPath = '/auth/logout'
const defaultTracksData = { tracks: { new: [], heard: [], recentlyAdded: [] }, meta: { totalTracks: 0, newTracks: 0 } }

const CART_TRACKS_PAGE_SIZE = 20
// The API names of the user's track lists, by list state.
const USER_TRACK_LISTS = { new: 'new', heard: 'heard', recent: 'recentlyAdded' }
// Cart search details when the cart is not found (meta.cartSearch is then null).
const emptyCartSearchResult = {
  k: 0,
  autoK: 0,
  maxK: 0,
  groups: [],
  map: { members: [] },
  excluded: { heard: 0, ignored: 0, purchased: 0, knownArtists: 0 },
  cartTracks: null,
}

const buildLoginReturnPath = () => {
  const params = new URLSearchParams(window.location.search)
  params.delete('loginFailed')
  const query = params.toString()
  return `${window.location.pathname}${query ? `?${query}` : ''}`
}

const deduplicateTracks = (existingTracks, newTracks) => {
  const existingIds = new Set(existingTracks.map(track => track.id))
  const uniqueNewTracks = newTracks.filter(track => !existingIds.has(track.id))
  return [...existingTracks, ...uniqueNewTracks]
}

// Returns a new track row with `carts` patched: { uuid } appended (op === 'add')
// or filtered out (op === 'remove'). Idempotent — adding an already-present
// uuid is a no-op, removing an absent uuid is a no-op. Always returns a fresh
// object so React state updates are observable; `carts` is always normalised
// to an array (defaults to [] if absent or non-array on input).
export const patchTrackCarts = (track, cartUuid, op) => {
  if (!track || typeof track !== 'object') return track
  const existing = Array.isArray(track.carts) ? track.carts : []
  if (op === 'add') {
    if (existing.some((c) => c && c.uuid === cartUuid)) return { ...track, carts: existing }
    return { ...track, carts: existing.concat({ uuid: cartUuid }) }
  }
  if (op === 'remove') {
    if (!existing.some((c) => c && c.uuid === cartUuid)) return { ...track, carts: existing }
    return { ...track, carts: existing.filter((c) => !c || c.uuid !== cartUuid) }
  }
  return track
}

// Walks every track-row-bearing slice of state, applies patchTrackCarts to
// rows whose id matches `trackId`, and returns the patched slices. Slices that
// don't contain the affected track are returned unchanged (referentially
// equal) so React reconciliation skips them.
export const patchTrackCartMembership = (slices, trackId, cartUuid, op) => {
  const patchArray = (arr) => {
    if (!Array.isArray(arr)) return arr
    let changed = false
    const out = arr.map((t) => {
      if (!t || t.id !== trackId) return t
      const patched = patchTrackCarts(t, cartUuid, op)
      if (patched !== t) changed = true
      return patched
    })
    return changed ? out : arr
  }
  return {
    new: patchArray(slices.new),
    heard: patchArray(slices.heard),
    recentlyAdded: patchArray(slices.recentlyAdded),
    searchResults: patchArray(slices.searchResults),
    selectedCartTracks: patchArray(slices.selectedCartTracks),
  }
}

// A cart response ({ cart, tracks, page }) as the cart record kept in the state: the cart's fields with its tracks.
export const cartRecord = ({ cart, tracks }) => ({ ...cart, tracks })

// Merges PATCH /carts/:id/tracks response onto an existing cart record without
// replacing the in-memory `tracks` array wholesale. The PATCH response carries
// the cart's first page; we keep the user's already-loaded tracks (which may
// extend past the first page after infinite-scroll) and splice the single
// added/removed row in place. Top-level metadata (name, storeDetails,
// trackCount, etc.) is taken from the PATCH response.
export const mergeCartDetailsPreservingTracks = (existing, response, { addedTrackId, removedTrackId } = {}) => {
  const existingTracks = (existing && existing.tracks) || []
  let nextTracks = existingTracks
  if (removedTrackId != null) {
    const idx = existingTracks.findIndex((t) => t && t.id === removedTrackId)
    if (idx !== -1) {
      nextTracks = existingTracks.slice(0, idx).concat(existingTracks.slice(idx + 1))
    }
  } else if (addedTrackId != null) {
    const responseTracks = (response && response.tracks) || []
    const newRow = responseTracks.find((t) => t && t.id === addedTrackId)
    if (newRow && !existingTracks.some((t) => t && t.id === addedTrackId)) {
      nextTracks = [newRow].concat(existingTracks)
    }
  }
  return { ...existing, ...response, tracks: nextTracks }
}

const Root = (props) => <div className="root" style={{ height: '100vh' }} {...props} />

class LocationWatcherBase extends Component {
  componentDidMount() {
    this.props.onLocationChange(this.props.location)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location !== this.props.location) {
      this.props.onLocationChange(this.props.location)
    }
  }

  render() {
    return null
  }
}

const LocationWatcher = withRouter(LocationWatcherBase)

class App extends Component {
  constructor(props) {
    super(props)
    const { listState, currentTrack } = JSON.parse(window.localStorage.getItem('currentTrack')) || {}

    const searchParams = new URLSearchParams(window.location.search)
    const filters = {
      sort: searchParams.get('sort') || '-released',
      limit: searchParams.get('limit') || 100,
      addedSince: searchParams.get('addedSince') || '',
      onlyNew: searchParams.get('onlyNew') || '',
    }

    this.searchEventHandler = async function (params) {
      const { q, ...rest } = params.detail
      const searchTerms = parseSearchTerms(q)
      this.setState({ searchTerms, searchFilters: rest })
      await this.search(searchTerms, { ...filters, ...rest })
    }.bind(this)

    const searchQuery = searchParams.get('q') || ''
    const searchTerms = applyEntityNamesFromUrlParam(
      parseSearchTerms(searchQuery),
      searchParams.get('names') || '',
    )
    const isSearchPath = window.location.pathname.match(/^\/search\/?$/)
    const initialListState = searchTerms.length > 0 && isSearchPath ? 'search' : (listState || 'new')

    this.state = {
      launchMessage: launchMessages[Math.floor(Math.random() * launchMessages.length)],
      carts: [],
      stores: [],
      scoreWeights: {},
      notifications: [],
      loggedIn: false,
      loading: true,
      tracksData: defaultTracksData,
      initialPosition: undefined,
      processingCart: false,
      processingTrack: null,
      fetchingCartDetails: false,
      userSettings: {},
      isAdmin: false,
      isMobile,
      onboarding: false,
      searchTerms,
      searchFilters: filters,
      searchInProgress: false,
      searchError: undefined,
      searchResults: [],
      cartSearch: null,
      searchBarResetCount: 0,
      listState: initialListState,
      currentTrack,
      heardTracks: defaultTracksData.tracks.heard,
      selectedCartUuid: undefined,
      selectedCart: undefined,
      mode: undefined,
      loadingMore: false,
      trackOffsets: { new: 0, heard: 0, recent: 0, search: 0 },
      // The latest page ({ offset, limit, total }) of each list, by list state.
      pages: {},
      cartPagination: null,
      notHeardBefore: null,
    }

    this.lastLocationSignature = ''
  }

  setListState(listState) {
    this.setState({ listState })
    window.history.replaceState(undefined, undefined, `/${listState}`)
  }

  hasMoreTracks() {
    const { listState, pages, trackOffsets, cartPagination } = this.state
    if (['new', 'heard', 'recent', 'search'].includes(listState)) {
      const page = pages[listState]
      return page ? trackOffsets[listState] < page.total : false
    } else if (listState === 'carts') {
      return cartPagination ? cartPagination.offset + cartPagination.count < cartPagination.total : false
    }
    return true
  }

  async loadMoreTracks() {
    const { listState, loadingMore, cartPagination, selectedCartUuid, selectedCart } = this.state
    if (!this.hasMoreTracks()) return

    if (listState === 'search') {
      if (loadingMore) return
      await this.search(this.state.searchTerms, this.state.searchFilters, true)
    } else if (['new', 'heard', 'recent'].includes(listState)) {
      if (loadingMore) return
      this.setState({ loadingMore: true })
      await this.updateTracks(true)
    } else if (listState === 'carts') {
      if (!cartPagination || cartPagination.loadingMore || !selectedCartUuid || !selectedCart) return
      this.setState({ cartPagination: { ...cartPagination, loadingMore: true } })
      const nextOffset = cartPagination.offset + cartPagination.count
      const { cart, tracks: appendedTracks, page } = await requestJSONwithCredentials({
        path: `/carts/${selectedCartUuid}?offset=${nextOffset}&limit=${CART_TRACKS_PAGE_SIZE}`,
      })
      const carts = this.state.carts.slice()
      const cartIndex = carts.findIndex(({ uuid }) => uuid === selectedCartUuid)
      if (cartIndex !== -1) {
        const existingTracks = carts[cartIndex].tracks || []
        carts[cartIndex] = { ...carts[cartIndex], ...cart, tracks: existingTracks.concat(appendedTracks) }
      }
      const updatedSelectedCart = cartIndex !== -1 ? carts[cartIndex] : this.state.selectedCart
      this.setState({
        carts,
        selectedCart: updatedSelectedCart,
        cartPagination: {
          offset: 0,
          count: cartPagination.count + appendedTracks.length,
          total: page.total,
          loadingMore: false,
        },
      })
    }
  }

  async updateStatesFromServer() {
    await Promise.all([
      this.updateTracks(),
      this.updateCarts(),
      this.updateScoreWeights(),
      this.updateFollows(),
      this.updateNotifications(),
      this.updateSettings(),
      this.updateUserDetails(),
    ])
  }

  async componentWillUnmount() {
    unsubscribe(events.SEARCH, this.searchEventHandler)
  }

  async componentDidMount() {
    subscribe(events.SEARCH, this.searchEventHandler)
    this.syncStateWithLocation()


    const pathParts = location.pathname.slice(1).split('/')
    const isCartPath = pathParts[0] === 'carts'
    let sharedStates
    let cartSelectPromise = Promise.resolve()

    const storeFetchPromise = requestJSONwithCredentials({
      path: `/stores`,
    })

    if (isCartPath && pathParts.length > 1 && pathParts[1] !== '') {
      const cartUuid = pathParts[1]
      // TODO: this causes a race condition with the initial cart fetch causing only the selected cart to be visible in the UI
      cartSelectPromise = this.selectCart(cartUuid)

      const initialPosition = parseInt(location.hash.substring(1)) || undefined
      sharedStates = { initialPosition }
    }

    try {
      const [stores] = await Promise.all([storeFetchPromise, cartSelectPromise, this.updateStatesFromServer()])
      if (stores.length === 1) {
        document.title = `Fomo Player - ${stores[0].storeName}`
      }

      this.setState({ loggedIn: true, mode: 'app', stores, ...sharedStates })

      const searchParams = new URLSearchParams(window.location.search)
      const searchQuery = searchParams.get('q') || ''
      const searchTerms = applyEntityNamesFromUrlParam(
        parseSearchTerms(searchQuery),
        searchParams.get('names') || '',
      )
      const isSearchPath = window.location.pathname.match(/^\/search\/?$/)
      if (searchTerms.length > 0 && isSearchPath) {
        const filters = {
          sort: searchParams.get('sort') || '-released',
          limit: searchParams.get('limit') || 100,
          addedSince: searchParams.get('addedSince') || '',
          onlyNew: searchParams.get('onlyNew') || '',
        }
        await this.search(searchTerms, filters)
      }
    } catch (e) {
      if (e.response?.status === 401 && isCartPath) {
        const stores = await storeFetchPromise
        this.setState({ mode: 'list', stores, ...sharedStates })
      } else {
        console.error(e)
      }
      this.setState({
        loggedIn: false,
      })
    } finally {
      this.setState({ loading: false })
    }
  }

  componentDidUpdate() {
    this.syncStateWithLocation()
  }

  syncStateWithLocation(location = window.location) {
    const { pathname, search } = location
    const locationSignature = `${pathname}${search}`
    if (locationSignature === this.lastLocationSignature) return
    this.lastLocationSignature = locationSignature

    const pathParts = pathname.slice(1).split('/')
    const searchParams = new URLSearchParams(search)
    const query = searchParams.get('q') || ''
    const isSearchPath = pathname.match(/^\/search\/?$/)

    const nextState = {}
    let shouldSetState = false

    if (isSearchPath && query) {
      const searchTerms = applyEntityNamesFromUrlParam(
        parseSearchTerms(query),
        searchParams.get('names') || '',
      )
      const filters = {
        sort: searchParams.get('sort') || '-released',
        limit: searchParams.get('limit') || 100,
        addedSince: searchParams.get('addedSince') || '',
        onlyNew: searchParams.get('onlyNew') || '',
      }
      const currentQuery = searchTermsToQueryString(this.state.searchTerms || [])
      if (currentQuery !== query || this.state.listState !== 'search') {
        nextState.searchTerms = searchTerms
        nextState.searchFilters = filters
        nextState.listState = 'search'
        shouldSetState = true
        this.search(searchTerms, filters)
      }
    } else if (this.state.searchTerms.length > 0) {
      nextState.searchTerms = []
      shouldSetState = true
    }

    const nextListState = pathParts[0] === 'tracks' ? (pathParts[1] || 'new') : pathParts[0]
    if (nextListState && nextListState !== this.state.listState && nextListState !== 'search') {
      nextState.listState = nextListState
      shouldSetState = true
    }

    if (nextListState === 'carts') {
      const selectedCartUuid = pathParts[1]
      const selectedCart = this.state.carts?.find(({ uuid }) => uuid === selectedCartUuid)
      if (
        selectedCartUuid &&
        (this.state.selectedCartUuid !== selectedCartUuid || this.state.selectedCart !== selectedCart)
      ) {
        nextState.selectedCartUuid = selectedCartUuid
        nextState.selectedCart = selectedCart
        shouldSetState = true
      }
    }

    if (shouldSetState) {
      this.setState(nextState)
    }
  }

  async onLoginDone() {
    this.setState({ loggedIn: true })
    await this.updateStatesFromServer()
  }

  onLogoutDone() {
    this.setState({ loggedIn: false, tracksData: defaultTracksData })
  }

  async updateCarts() {
    const carts = await requestJSONwithCredentials({
      path: `/me/carts`,
    })
    const visibleCarts = carts.filter(({ deleted }) => !deleted)
    const selectedCartUuid = this.state.selectedCartUuid || visibleCarts[0]?.uuid
    const selectedCart = selectedCartUuid ? visibleCarts.find(({ uuid }) => uuid === selectedCartUuid) : undefined

    this.setState({
      carts: visibleCarts,
      selectedCartUuid,
      selectedCart,
    })
  }

  async updateScoreWeights() {
    const scoreWeights = await requestJSONwithCredentials({
      path: '/me/score-weights',
    })
    this.setState({ scoreWeights })
  }

  async addToCart(cartId, trackId) {
    const cartDetails = cartRecord(
      await requestJSONwithCredentials({
        path: `/me/carts/${cartId}/tracks`,
        method: 'PATCH',
        body: [{ op: 'add', trackId }],
      }),
    )

    this.applyCartMutation(cartDetails, { trackId, cartId, op: 'add' })
  }

  async removeFromCart(cartId, trackId) {
    const cartDetails = cartRecord(
      await requestJSONwithCredentials({
        path: `/me/carts/${cartId}/tracks`,
        method: 'PATCH',
        body: [{ op: 'remove', trackId }],
      }),
    )

    this.applyCartMutation(cartDetails, { trackId, cartId, op: 'remove' })
  }

  applyCartMutation(cartDetails, { trackId, cartId, op }) {
    const trackSlices = this.state.tracksData.tracks
    const cartUuid = cartDetails && cartDetails.uuid
      ? cartDetails.uuid
      : (this.state.carts.find((c) => c.id === cartId) || {}).uuid
    const patched = patchTrackCartMembership(
      {
        new: trackSlices.new,
        heard: trackSlices.heard,
        recentlyAdded: trackSlices.recentlyAdded,
        searchResults: this.state.searchResults,
        selectedCartTracks: this.state.selectedCart && this.state.selectedCart.tracks,
      },
      trackId,
      cartUuid,
      op,
    )

    const cartIndex = this.state.carts.findIndex(R.propEq(cartDetails.id, 'id'))
    const partialState = {}

    if (cartIndex !== -1) {
      const existingCart = this.state.carts[cartIndex]
      const existingTracksForMerge =
        this.state.selectedCart && this.state.selectedCart.id === cartDetails.id
          ? patched.selectedCartTracks
          : existingCart.tracks
      const merged = mergeCartDetailsPreservingTracks(
        { ...existingCart, tracks: existingTracksForMerge },
        cartDetails,
        {
          addedTrackId: op === 'add' ? trackId : undefined,
          removedTrackId: op === 'remove' ? trackId : undefined,
        },
      )
      const clonedCarts = this.state.carts.slice()
      clonedCarts[cartIndex] = merged
      partialState.carts = clonedCarts
      if (this.state.selectedCart && this.state.selectedCart.id === merged.id) {
        partialState.selectedCart = merged
        if (this.state.cartPagination) {
          const total =
            merged.trackCount != null
              ? merged.trackCount
              : this.state.cartPagination.total + (op === 'add' ? 1 : -1)
          const count = merged.tracks ? merged.tracks.length : this.state.cartPagination.count
          partialState.cartPagination = { ...this.state.cartPagination, total, count }
        }
      }
    }

    if (patched.new !== trackSlices.new || patched.heard !== trackSlices.heard || patched.recentlyAdded !== trackSlices.recentlyAdded) {
      partialState.tracksData = {
        ...this.state.tracksData,
        tracks: { new: patched.new, heard: patched.heard, recentlyAdded: patched.recentlyAdded },
      }
    }
    if (patched.heard !== trackSlices.heard) partialState.heardTracks = patched.heard
    if (patched.searchResults !== this.state.searchResults) partialState.searchResults = patched.searchResults

    if (Object.keys(partialState).length > 0) this.setState(partialState)
  }

  async onMarkPurchased(trackId) {
    this.setState({ processingCart: true, processingTrack: trackId })
    try {
      await requestJSONwithCredentials({
        path: `/me/purchased/`,
        method: 'POST',
        body: [{ trackId }],
      })
      await requestWithCredentials({
        path: `/me/carts/`,
        method: 'PATCH',
        body: [{ op: 'remove', trackId }],
      })
      await Promise.all([this.updateTracks(), this.selectCart(this.state.selectedCartUuid)])
    } catch (e) {
      console.error(`Marking track as purchased failed: ${e.toString()}`)
    }
    this.setState({ processingCart: false, processingTrack: null })
  }

  async updateFollows() {
    const [artists, labels] = await Promise.all([
      requestJSONwithCredentials({
        path: `/me/follows/artists`,
      }),
      await requestJSONwithCredentials({
        path: `/me/follows/labels`,
      }),
    ])
    this.setState({ follows: { artists, labels } })
  }

  async requestNotificationUpdate(operations) {
    const notifications = await requestJSONwithCredentials({
      path: `/me/notifications`,
      method: 'PATCH',
      body: operations,
    })

    this.setState({ notifications })
  }

  async setStarred(type, followId, starred) {
    await requestWithCredentials({
      path: `/me/follows/${type}/${followId}`,
      method: 'PUT',
      body: {
        starred,
      },
    })

    await this.updateNotifications()
  }

  async updateNotifications() {
    this.setState({ notifications: await requestJSONwithCredentials({ path: '/me/notifications' }) })
  }

  async updateTracks(append = false) {
    const { trackOffsets, listState, notHeardBefore } = this.state
    const notHeardBeforeParam = notHeardBefore ? `notHeardBefore=${encodeURIComponent(notHeardBefore)}` : ''

    if (append) {
      const list = USER_TRACK_LISTS[listState]
      if (!list) {
        this.setState({ loadingMore: false })
        return
      }
      const params = [`offset=${trackOffsets[listState]}`, 'limit=20', notHeardBeforeParam].filter(Boolean).join('&')
      const { tracks, page } = await requestJSONwithCredentials({ path: `/me/tracks/${list}?${params}` })
      const existing = this.state.tracksData.tracks[list]
      const updatedTracks = deduplicateTracks(existing, tracks)
      this.setState({
        tracksData: {
          ...this.state.tracksData,
          tracks: { ...this.state.tracksData.tracks, [list]: updatedTracks },
        },
        ...(list === 'heard' ? { heardTracks: deduplicateTracks(this.state.heardTracks, tracks) } : {}),
        trackOffsets: { ...this.state.trackOffsets, [listState]: page.offset + tracks.length },
        pages: { ...this.state.pages, [listState]: page },
        loadingMore: false,
      })
      return
    }

    const { lists, meta } = await requestJSONwithCredentials({
      path: `/me/tracks${notHeardBeforeParam ? `?${notHeardBeforeParam}` : ''}`,
    })
    if (meta.notHeardBefore && !this.state.notHeardBefore) {
      this.setState({ notHeardBefore: meta.notHeardBefore })
    }
    const tracks = R.map(R.prop('tracks'), lists)
    this.setState({
      tracksData: { tracks, meta: { newTracks: lists.new.page.total, totalTracks: meta.totalTracks } },
      heardTracks: tracks.heard,
      onboarding: tracks.new.length === 0 && tracks.heard.length === 0,
      trackOffsets: {
        new: lists.new.page.offset + tracks.new.length,
        heard: lists.heard.page.offset + tracks.heard.length,
        recent: lists.recentlyAdded.page.offset + tracks.recentlyAdded.length,
        search: this.state.trackOffsets.search,
      },
      pages: { ...this.state.pages, new: lists.new.page, heard: lists.heard.page, recent: lists.recentlyAdded.page },
    })
  }

  async markHeard(track) {
    if (this.state.listState === 'heard' || this.state.mode === 'list') {
      return
    }

    // TODO: if the tracks are always updated, the list refreshes -> problem?
    // await this.updateTracks()

    let updatedHeardTracks = this.state.heardTracks
    const updatedTrack = R.assoc('heard', (new Date()).toISOString(), track)
    const playedTrackIndex = this.state.heardTracks.findIndex(R.propEq(track.id, 'id'))
    if (playedTrackIndex !== -1) {
      updatedHeardTracks.splice(playedTrackIndex, 1)
    } else {
      // TODO: Probably safer to not store the count, but instead calculate it from the array length
      this.setState({ listenedTracks: this.state.listenedTracks + 1 })
    }

    updatedHeardTracks = R.prepend(updatedTrack, updatedHeardTracks)
    this.setState({ heardTracks: updatedHeardTracks })

    // TODO: do this in the background? Although this should not block the UI either
    await requestWithCredentials({
      path: `/me/tracks/${track.id}`,
      method: 'POST',
      body: { heard: true },
    })
  }

  // Toggle a track's heard state (used by the mobile swipe gesture). Unlike
  // markHeard this can also clear the heard flag, and it keeps the displayed
  // lists in sync so the change (e.g. the "new" indicator) is reflected
  // immediately and the gesture can be repeated to undo it.
  async setHeard(track, heard) {
    if (!track || this.state.mode === 'list') {
      return
    }

    const heardAt = heard ? new Date().toISOString() : null
    const { tracksData, heardTracks } = this.state
    // heardTracks ids can be strings while list ids are numbers (see
    // mergeHeardStatus' parseInt), so compare on a normalised id.
    const sameTrack = (t) => String(t.id) === String(track.id)
    const wasInHeard = heardTracks.some(sameTrack)

    const updateHeardInList = (list) =>
      (list || []).map((t) => (sameTrack(t) ? { ...t, heard: heardAt } : t))

    let updatedHeardTracks = heardTracks.filter((t) => !sameTrack(t))
    if (heard) {
      updatedHeardTracks = R.prepend({ ...track, heard: heardAt }, updatedHeardTracks)
    }

    const listenedDelta = heard ? (wasInHeard ? 0 : 1) : wasInHeard ? -1 : 0

    this.setState({
      heardTracks: updatedHeardTracks,
      listenedTracks: this.state.listenedTracks + listenedDelta,
      tracksData: {
        ...tracksData,
        tracks: {
          ...tracksData.tracks,
          new: updateHeardInList(tracksData.tracks.new),
          heard: updateHeardInList(tracksData.tracks.heard),
          recentlyAdded: updateHeardInList(tracksData.tracks.recentlyAdded),
        },
      },
    })

    await requestWithCredentials({
      path: `/me/tracks/${track.id}`,
      method: 'POST',
      body: { heard },
    })
  }

  async updateEmail(email) {
    await requestWithCredentials({
      path: `/me/settings`,
      method: 'POST',
      body: { email },
    })
    await this.updateSettings()
  }

  async createCart(cartName) {
    return await requestJSONwithCredentials({
      path: `/me/carts`,
      method: 'POST',
      body: { name: cartName },
    })
  }

  async updateSettings() {
    const userSettings = await requestJSONwithCredentials({
      path: `/me/settings`,
    })
    this.setState({ userSettings })
  }

  async updateUserDetails() {
    try {
      const { isAdmin } = await requestJSONwithCredentials({ path: `/me` })
      this.setState({ isAdmin })
    } catch (e) {
      console.error('Failed to fetch user details', e)
    }
  }

  onOnboardingButtonClicked() {
    this.setState({
      onboarding: !this.state.onboarding,
    })
  }

  setFollowPopupOpen(open) {
    this.setState({ followPopupOpen: open })
  }

  openFollowPopup(track) {
    this.setState({ followPopupTrack: track })
    this.setFollowPopupOpen(true)
  }

  setIgnorePopupOpen(open) {
    this.setState({ ignorePopupOpen: open })
  }

  openIgnorePopup(track) {
    this.setState({ ignorePopupTrack: track })
    this.setIgnorePopupOpen(true)
  }

  closePopups() {
    this.setFollowPopupOpen(false)
    this.setIgnorePopupOpen(false)
    this.setKeyboardShortcutsPopupOpen(false)
  }

  openKeyboardShortcutsPopup() {
    this.setKeyboardShortcutsPopupOpen(true)
  }

  setKeyboardShortcutsPopupOpen(open) {
    this.setState({ keyboardShortcutsPopupOpen: open })
  }

  async handleCartButtonClick(trackId, cartId, inCart) {
    if (inCart) {
      this.setState({ processingCart: true, processingTrack: trackId })
      try {
        await this.removeFromCart(cartId, trackId)
      } catch (e) {
        console.error('Error while removing from cart', e)
      } finally {
        this.setState({ processingCart: false, processingTrack: null })
      }
    } else {
      this.setState({ processingCart: true, processingTrack: trackId })
      try {
        await this.addToCart(cartId, trackId)
      } catch (e) {
        console.error('Error while adding to cart', e)
      } finally {
        this.setState({ processingCart: false, processingTrack: null })
      }
    }
  }

  async handleCreateCartClick(cartName) {
    try {
      this.setState({ processingCart: true })
      const res = await this.createCart(cartName)
      this.setState({ newCartName: '' })
      await this.updateCarts()
      return res
    } catch (e) {
      console.error('Error while creating new cart', e)
    } finally {
      this.setState({ processingCart: false })
    }
  }

  async handleToggleNotificationClick(searchTerms, subscribe, storeNames = undefined) {
    let operations = []
    try {
      const searchString = searchTermsToString(searchTerms)
      if (storeNames === undefined) {
        if (subscribe) {
          operations = operations.concat(
            this.state.stores.map(({ storeName }) => ({
              op: 'add',
              storeName,
              text: searchString,
            })),
          )
        } else {
          operations = operations.concat(
            this.state.stores.map(({ storeName }) => ({ op: 'remove', storeName, text: searchString })),
          )
        }
      } else {
        storeNames.forEach((storeName) => {
          operations.push({ op: subscribe ? 'add' : 'remove', storeName, text: searchString })
        })
      }

      await this.requestNotificationUpdate(operations)
    } finally {
      this.setState({ modifyingNotification: false })
    }
  }

  addEntityToSearch(entityType, entityId, entityName, append = false) {
    const baseSearchTerms = append ? (this.state.searchTerms || []) : []
    const newSearchTerms = addEntityTerm(baseSearchTerms, entityType, entityId, entityName)
    this.setState({ searchTerms: newSearchTerms })
    this.search(newSearchTerms, this.state.searchFilters)
  }

  async search(searchTerms, filters = {}, append = false) {
    const { sort = '-released', limit = 100, addedSince = null, onlyNew = null } = filters
    console.log({ onlyNew, filters })

    const query = searchTermsToQueryString(searchTerms)
    if (query === '') return
    const cartTerm = findCartSearchTerm(searchTerms)
    if (cartTerm) {
      if (!append) await this.startCartSearch(searchTerms, cartTerm, filters)
      return
    }
    if (this.state.cartSearch) this.setState({ cartSearch: null })
    // Only the newest search (this or a cart search) may update the list.
    const requestId = (this.searchRequestId = (this.searchRequestId || 0) + 1)
    const { trackOffsets } = this.state
    const offset = append ? trackOffsets.search : 0
    if (!append) {
      this.setState({ listState: 'search', searchInProgress: true, searchError: undefined, searchTerms, trackOffsets: { ...trackOffsets, search: 0 } })
    } else {
      this.setState({ loadingMore: true })
    }
    const names = entityNamesToUrlParam(searchTerms)
    const parameters = `q=${encodeURIComponent(query)}${names ? `&names=${encodeURIComponent(names)}` : ''}&sort=${sort || ''}&addedSince=${addedSince || ''}&onlyNew=${onlyNew || ''}&limit=${limit || ''}&offset=${offset}`
    if (!append) {
      window.history.pushState(undefined, undefined, `/search?${parameters}`)
    }
    try {
      const { tracks: searchResults, page } = await (
        await requestWithCredentials({
          path: `/tracks?${parameters}`,
        })
      ).json()
      if (requestId !== this.searchRequestId) {
        if (append) this.setState({ loadingMore: false })
        return undefined
      }
      if (append) {
        const existingSearchResults = this.state.searchResults
        const uniqueSearchResults = deduplicateTracks(existingSearchResults, searchResults)
        this.setState({
          searchResults: uniqueSearchResults,
          trackOffsets: { ...this.state.trackOffsets, search: page.offset + searchResults.length },
          pages: { ...this.state.pages, search: page },
          loadingMore: false,
        })
      } else {
        this.setState({
          searchResults,
          searchError: undefined,
          trackOffsets: { ...this.state.trackOffsets, search: page.offset + searchResults.length },
          pages: { ...this.state.pages, search: page },
        })
      }
      return undefined
    } catch (e) {
      if (requestId !== this.searchRequestId) {
        if (append) this.setState({ loadingMore: false })
        return undefined
      }
      console.error('Search failed', e)
      this.setState({ searchError: 'Search failed, please try again.', loadingMore: false })
    } finally {
      if (!append && requestId === this.searchRequestId) {
        this.setState({ searchInProgress: false })
      }
    }
  }

  // Cart similarity search: the cart is split into groups of similar tracks and each group is searched separately.
  // It runs through the track search like any other query, so the other search terms (text, artist, label, …) filter
  // the results. The settings below live only in this browser session; "Not this" tracks are sent with every request
  // and never stored on the server.
  async startCartSearch(searchTerms, cartTerm, filters = {}) {
    const cart = this.state.carts.find(({ uuid }) => uuid === cartTerm.id)
    const name = cartTerm.name || cart?.name
    const terms = searchTerms.map((term) => (term === cartTerm && name ? { ...term, name } : term))
    const query = searchTermsToQueryString(terms)
    const names = entityNamesToUrlParam(terms)
    window.history.pushState(
      undefined,
      undefined,
      `/search?q=${encodeURIComponent(query)}${names ? `&names=${encodeURIComponent(names)}` : ''}`,
    )
    const cartSearch = {
      uuid: cartTerm.id,
      name,
      query,
      addedSince: filters.addedSince || null,
      k: null,
      newArtistsOnly: false,
      misses: [],
      chip: 'all',
      mapOpen: false,
      saved: [],
      result: null,
      toast: '',
    }
    this.setState({ listState: 'search', searchTerms: terms, searchError: undefined, searchResults: [], cartSearch })
    await this.fetchCartSearch(cartSearch)
  }

  visibleCartSearchResults(cartSearch) {
    const tracks = cartSearch?.result?.tracks || []
    const missed = new Set(cartSearch.misses.map(({ id }) => id))
    return tracks.filter(
      (track) => !missed.has(track.id) && (cartSearch.chip === 'all' || track.cartSearch?.group === cartSearch.chip),
    )
  }

  async fetchCartSearch(cartSearch) {
    const requestId = (this.searchRequestId = (this.searchRequestId || 0) + 1)
    const params = new URLSearchParams({ q: cartSearch.query })
    if (cartSearch.k !== null && cartSearch.k !== undefined) params.set('k', cartSearch.k)
    if (cartSearch.newArtistsOnly) params.set('newArtistsOnly', 'true')
    if (cartSearch.misses.length > 0) params.set('misses', cartSearch.misses.map(({ id }) => id).join(','))
    if (cartSearch.addedSince) params.set('addedSince', cartSearch.addedSince)
    this.setState({ searchInProgress: true, searchError: undefined })
    try {
      const { tracks, page, meta } = await requestJSONwithCredentials({ path: `/tracks?${params.toString()}` })
      if (requestId !== this.searchRequestId) return
      const result = meta.cartSearch
        ? { ...meta.cartSearch, tracks }
        : { ...emptyCartSearchResult, tracks, reason: 'Cart not found.' }
      const next = { ...this.state.cartSearch, ...cartSearch, result, k: result.k }
      if (next.chip !== 'all' && next.chip >= result.groups.length) next.chip = 'all'
      this.setState({
        cartSearch: next,
        searchResults: this.visibleCartSearchResults(next),
        searchError: result.reason,
        trackOffsets: { ...this.state.trackOffsets, search: page.offset + tracks.length },
        pages: { ...this.state.pages, search: page },
      })
    } catch (e) {
      if (requestId !== this.searchRequestId) return
      console.error('Cart search failed', e)
      this.setState({ searchError: 'Search failed, please try again.' })
    } finally {
      if (requestId === this.searchRequestId) this.setState({ searchInProgress: false })
    }
  }

  // Changes that only affect what is shown (chip, map) are applied locally; the rest re-run the search.
  async updateCartSearch(changes) {
    const current = this.state.cartSearch
    if (!current) return
    const next = { ...current, ...changes }
    if ('k' in changes && changes.k !== current.k) next.chip = 'all'
    const refetch = ['k', 'newArtistsOnly', 'misses'].some((key) => key in changes && changes[key] !== current[key])
    this.setState({ cartSearch: next, searchResults: this.visibleCartSearchResults(next) })
    if (refetch) await this.fetchCartSearch(next)
  }

  async markCartSearchMiss(track) {
    const current = this.state.cartSearch
    if (!current || current.misses.some(({ id }) => id === track.id)) return
    const label = `${(track.artists || []).map(({ name }) => name)[0] || ''} – ${track.title}`
    await this.updateCartSearch({
      misses: [...current.misses, { id: track.id, label }],
      toast: `Hidden ${label} and pushed the search away from it.`,
    })
  }

  async saveCartSearchGroup(groupIndex, cartName) {
    const current = this.state.cartSearch
    const group = current?.result?.groups?.[groupIndex]
    if (!group || !cartName) return
    const trackIds = current.result.map.members.filter((m) => m.group === groupIndex).map(({ trackId }) => trackId)
    await requestJSONwithCredentials({
      path: `/me/carts`,
      method: 'POST',
      body: { name: cartName, tracks: trackIds.map((trackId) => ({ trackId })) },
    })
    await this.updateCarts()
    const latest = this.state.cartSearch
    if (latest) {
      this.setState({
        cartSearch: {
          ...latest,
          saved: [...latest.saved, { k: latest.k, group: groupIndex, name: cartName }],
          toast: `Created cart “${cartName}” with ${trackIds.length} tracks.`,
        },
      })
    }
  }

  // Starts a fresh cart search: whatever was in the search bar is cleared.
  findSimilarToCart(cart) {
    if (!cart?.uuid) return
    const term = { type: 'cart', value: `cart:~${cart.uuid}`, id: cart.uuid, similar: true, name: cart.name }
    this.setState({ searchBarResetCount: this.state.searchBarResetCount + 1 })
    this.search([term], this.state.searchFilters)
  }

  logout = async () => {
    try {
      await requestWithCredentials({ path: logoutPath, method: 'POST' })
    } catch (e) {
      console.error('Logout failed', e)
    }
    this.onLogoutDone()
  }

  async triggerSearch() {
    return this.setSearch(this.state.searchTerms, true)
  }

  setSearch(searchTerms, triggerSearch = false) {}

  // TODO: change to POST {ignore: true} /me/labels/?
  async ignoreArtistsByLabels(artistId, labelIds, ignore) {
    await requestWithCredentials({
      path: `/me/ignores/artists-on-labels`,
      method: ignore ? 'POST' : 'DELETE',
      body: { artistIds: [artistId], labelIds },
    })
  }

  async ignoreArtist(artistId) {
    await requestWithCredentials({
      path: `/me/ignores/artists`,
      method: 'POST',
      body: [artistId],
    })
  }

  async ignoreLabel(labelId) {
    await requestWithCredentials({
      path: `/me/ignores/labels`,
      method: 'POST',
      body: [labelId],
    })
  }

  async ignoreRelease(releaseId) {
    await requestWithCredentials({
      path: `/me/ignores/releases`,
      method: 'POST',
      body: [releaseId],
    })
  }

  async selectCart(selectedCartUuid) {
    this.setState({ selectedCartUuid, fetchingCartDetails: true, cartPagination: null })
    const path = `/carts/${selectedCartUuid}?offset=0&limit=${CART_TRACKS_PAGE_SIZE}`
    const response = await requestJSONwithCredentials({ path })
    const cartDetails = cartRecord(response)
    let updatedCarts = this.state.carts.slice()
    let cartIndex = updatedCarts.findIndex(({ uuid }) => uuid === selectedCartUuid)
    if (cartIndex === -1) {
      cartIndex = updatedCarts.length
      updatedCarts.push(cartDetails)
    } else {
      updatedCarts[cartIndex] = cartDetails
    }

    this.setState({
      carts: updatedCarts,
      selectedCart: updatedCarts[cartIndex],
      fetchingCartDetails: false,
      cartPagination: {
        offset: 0,
        count: response.tracks.length,
        total: response.page.total,
        loadingMore: false,
      },
    })
  }

  async setCurrentTrack(track) {
    this.setState({ currentTrack: track })
    window.localStorage.setItem(
      'currentTrack',
      JSON.stringify({ listState: this.state.listState, currentTrack: track }),
    )
    document.title = `${trackArtistsAndTitleText(track)} - Fomo Player ${this.state.stores.length === 1 ? ` - ${this.state.stores[0].storeName}` : ''}`
  }

  async followStoreArtist(storeArtistId, storeArtistUrl, name, follow) {
    await requestWithCredentials({
      path: `/me/follows/artists/${follow ? '' : storeArtistId}`,
      method: follow ? 'POST' : 'DELETE',
      body: follow ? [{ url: storeArtistUrl, name }] : undefined,
    })

    await this.updateFollows()
  }

  async followStoreLabel(storeLabelId, storeLabelUrl, name, follow) {
    await requestWithCredentials({
      path: `/me/follows/labels/${follow ? '' : storeLabelId}`,
      method: follow ? 'POST' : 'DELETE',
      body: follow ? [{ url: storeLabelUrl, name }] : undefined,
    })

    await this.updateFollows()
  }

  async refreshListAndClosePopups() {
    await this.updateTracks()
    this.closePopups()
  }

  render() {
    const googleLoginPath = `${config.apiURL}/auth/login/google?returnPath=${encodeURIComponent(buildLoginReturnPath())}`

    return (
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={reportRenderError}>
        <CrashTest />
        <Root
          className={`${this.state.listState === 'search' ? 'search-expanded' : ''} ${
            this.state.isMobile ? 'mobile' : 'desktop'
          }`}
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            alignItems: 'stretch',
          }}
        >
          <Router>
            <LocationWatcher onLocationChange={this.syncStateWithLocation.bind(this)} />
            {this.state.loading ? (
              <div className="loading-overlay">
                <div className="loading-message">{this.state.launchMessage.text}</div>
                <div className="loading-emoji">{this.state.launchMessage.emoji}</div>
              </div>
            ) : !this.state.loggedIn ? (
              this.state.mode === 'list' ? (
                <Player
                  mode="list"
                  initialPosition={this.state.initialPosition}
                  onSetCurrentTrack={this.setCurrentTrack.bind(this)}
                  carts={this.state.carts}
                  selectedCart={this.state.selectedCart}
                  tracks={this.state.carts.find(({ uuid }) => uuid === this.state.selectedCartUuid)?.tracks || []}
                  heardTracks={this.state.heardTracks}
                  stores={this.state.stores}
                  currentTrack={this.state.currentTrack}
                  isMobile={this.state.isMobile}
                  markHeard={this.markHeard.bind(this)}
                  style={{ display: 'flex' }}
                />
              ) : (
                <div style={{ background: '#333', width: '100%', height: '100%' }}>
                  <div style={{ paddingTop: '3rem', maxWidth: '60ch', margin: 'auto' }}>
                    <h1 style={{ marginTop: 0, textAlign: 'center' }}>
                      Fomo Player
                      <br />
                      <div style={{ fontSize: '50%', fontWeight: 300 }}>
                        Never miss a beat!
                      </div>
                    </h1>
                    <div style={{ padding: '2rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <Login
                          onLoginDone={this.onLoginDone.bind(this)}
                          onLogoutDone={this.onLogoutDone.bind(this)}
                          googleLoginPath={googleLoginPath}
                          logoutPath={logoutPath}
                        />
                      </div>
                      <br />
                      {process.env.NODE_ENV !== 'production' && (
                        <p style={{ margin: '2rem' }}>
                          <form
                            onSubmit={(e) => {
                              e.preventDefault()
                              return this.onLoginDone()
                            }}
                          >
                            <label className="text-input">
                              Username
                              <input name="username" value={'testuser'} />
                            </label>
                            <br />
                            <label>
                              Password
                              <input name="password" value={'testpwd'} />
                            </label>
                            <br />
                            <input
                              type={'submit'}
                              value={'Login'}
                              data-test-id={'form-login-button'}
                              className="button button-push_button login-button button-push_button-large button-push_button-primary"
                            />
                          </form>
                        </p>
                      )}
                      <div className="login-separator">Want to know more?</div>
                      <p>
                        Fomo Player is a service for keeping up with new releases from your favorite artists and labels.
                        The service prioritises releases based on your preferences and keeps track of tracks you have
                        already listened to, thus improving the efficiency of your music discovery.
                      </p>
                      <p style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <a
                          href="https://github.com/gadgetmies/fomoplayer/wiki"
                          className={'button button-push_button button-push_button-large button-push_button-primary'}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Find out more on Github <FontAwesomeIcon icon={['fab', 'github']} />
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <>
                <Onboarding
                  newUser={this.state.tracksData.meta.totalTracks === 0}
                  active={this.state.onboarding}
                  onOnboardingEnd={() => {
                    this.setState({ onboarding: false })
                  }}
                />
                {this.state.follows ? (
                  <FollowPopup
                    open={this.state.followPopupOpen}
                    track={this.state.followPopupTrack}
                    follows={this.state.follows}
                    onCloseClicked={this.closePopups.bind(this)}
                    onFollowStoreArtist={this.followStoreArtist.bind(this)}
                    onFollowStoreLabel={this.followStoreLabel.bind(this)}
                    onRefreshAndCloseClicked={this.refreshListAndClosePopups.bind(this)}
                  />
                ) : null}
                <IgnorePopup
                  open={this.state.ignorePopupOpen}
                  track={this.state.ignorePopupTrack}
                  onCloseClicked={this.closePopups.bind(this)}
                  onIgnoreArtistOnLabels={this.ignoreArtistsByLabels.bind(this)}
                  onIgnoreArtist={this.ignoreArtist.bind(this)}
                  onIgnoreLabel={this.ignoreLabel.bind(this)}
                  onIgnoreRelease={this.ignoreRelease.bind(this)}
                  onRefreshAndCloseClicked={this.refreshListAndClosePopups.bind(this)}
                />
                <KeyboardShortcutsPopup
                  open={this.state.keyboardShortcutsPopupOpen}
                  mode={this.state.mode}
                  onCloseClicked={this.closePopups.bind(this)}
                />
                <TopBar
                  modifyingNotification={this.state.modifyingNotification}
                  emailVerified={this.state.userSettings.emailVerified}
                  triggerSearch={this.triggerSearch.bind(this)}
                  searchInProgress={this.state.searchInProgress}
                  searchActive={this.state.listState === 'search'}
                  onSearch={this.search.bind(this)}
                  onLogoutClicked={this.logout.bind(this)}
                  handleToggleNotificationClick={this.handleToggleNotificationClick.bind(this)}
                  listState={this.state.listState}
                  notifications={this.state.notifications}
                  searchTerms={this.state.searchTerms}
                  searchBarResetCount={this.state.searchBarResetCount}
                  searchFilters={this.state.searchFilters}
                  userSettings={this.state.userSettings}
                  isAdmin={this.state.isAdmin}
                  stores={this.state.stores}
                  carts={this.state.carts}
                  onSelectCart={this.selectCart.bind(this)}
                  onKeyboardShortcutsClicked={this.openKeyboardShortcutsPopup.bind(this)}
                  onOnboardingButtonClicked={this.onOnboardingButtonClicked.bind(this)}
                />

                <Route exact path="/">
                  <Redirect to="/tracks/new" />
                </Route>
                <Route exact path="/tracks">
                  <Redirect to="/tracks/new" />
                </Route>
                <Route exact path="/admin">
                  <Admin />
                </Route>
                <Route exact path="/admin/duplicates">
                  <AdminDuplicates />
                </Route>
                <Route exact path="/admin/mislabeled">
                  <AdminMislabeled />
                </Route>
                <Route exact path="/admin/artist-split">
                  <AdminArtistSplit />
                </Route>
                <Route exact path="/admin/jobs">
                  <AdminJobs />
                </Route>
                <Route exact path="/carts">
                  <Redirect to={`/carts/${this.state.carts[0].uuid}`} />
                </Route>
                <Route
                  path="/:path"
                  render={(props) => {
                    const {
                      location: { pathname },
                    } = window
                    // The admin views render via their own routes above; this
                    // catch-all also matches /admin*, so bail out here to avoid
                    // rendering the tracks list + player frame over them.
                    if (pathname.startsWith('/admin')) {
                      return null
                    }
                    const pathParts = pathname.slice(1).split('/')
                    const settingsVisible = pathname.match(/\/settings\/?/)
                    let listState = this.state.listState
                    const routeListState = pathParts[0] === 'tracks' ? pathParts[1] || 'new' : pathParts[0]
                    if (routeListState && routeListState !== 'settings') {
                      listState = routeListState
                    }

                    return (
                      <>
                        <Settings
                          carts={this.state.carts}
                          stores={this.state.stores}
                          onUpdateCarts={this.updateCarts.bind(this)}
                          notifications={this.state.notifications}
                          onRequestNotificationUpdate={this.requestNotificationUpdate.bind(this)}
                          onSetStarred={this.setStarred.bind(this)}
                          onMarkHeardClicked={this.markHeard.bind(this)}
                          onUpdateEmail={this.updateEmail.bind(this)}
                          onCreateCart={this.createCart.bind(this)}
                          newTracks={this.state.tracksData.meta.newTracks}
                          totalTracks={this.state.tracksData.meta.totalTracks}
                          userSettings={this.state.userSettings}
                          scoreWeights={this.state.scoreWeights}
                          tracks={this.state.tracksData.tracks}
                          follows={this.state.follows}
                          style={{ display: settingsVisible ? 'block' : 'none' }}
                        />
                        <Player
                          carts={this.state.carts}
                          fetchingCartDetails={this.state.fetchingCartDetails}
                          currentTrack={this.state.currentTrack}
                          follows={this.state.follows}
                          heardTracks={this.state.heardTracks}
                          initialPosition={this.state.initialPosition}
                          isMobile={this.state.isMobile}
                          listState={settingsVisible ? 'new' : listState}
                          mode="app"
                          newTracks={this.state.tracksData.meta.newTracks}
                          processingCart={this.state.processingCart}
                          processingTrack={this.state.processingTrack}
                          searchTerms={this.state.searchTerms || []}
                          searchError={this.state.searchError}
                          searchInProgress={this.state.searchInProgress}
                          searchResults={this.state.searchResults}
                          cartSearch={this.state.listState === 'search' ? this.state.cartSearch : null}
                          onCartSearchChange={this.updateCartSearch.bind(this)}
                          onCartSearchMiss={this.markCartSearchMiss.bind(this)}
                          onSaveCartSearchGroup={this.saveCartSearchGroup.bind(this)}
                          onFindSimilarToCart={this.findSimilarToCart.bind(this)}
                          selectedCart={this.state.listState === 'carts' ? this.state.selectedCart : undefined}
                          stores={this.state.stores}
                          totalTracks={this.state.tracksData.meta.totalTracks}
                          tracks={this.state.tracksData.tracks}
                          markHeard={this.markHeard.bind(this)}
                          setHeard={this.setHeard.bind(this)}
                          loadingMore={
                            listState === 'carts'
                              ? !!(this.state.cartPagination && this.state.cartPagination.loadingMore)
                              : this.state.loadingMore
                          }
                          hasMore={this.hasMoreTracks()}
                          onAddToCart={this.addToCart.bind(this)}
                          onClosePopups={this.closePopups.bind(this)}
                          onCreateCart={this.createCart.bind(this)}
                          onHandleCartButtonClick={this.handleCartButtonClick.bind(this)}
                          onHandleCreateCartClick={this.handleCreateCartClick.bind(this)}
                          onIgnoreArtist={this.ignoreArtist.bind(this)}
                          onIgnoreArtistsByLabels={this.ignoreArtistsByLabels.bind(this)}
                          onIgnoreLabel={this.ignoreLabel.bind(this)}
                          onIgnoreRelease={this.ignoreRelease.bind(this)}
                          onLoadMore={this.loadMoreTracks.bind(this)}
                          onMarkPurchased={this.onMarkPurchased.bind(this)}
                          onOpenFollowPopup={this.openFollowPopup.bind(this)}
                          onOpenIgnorePopup={this.openIgnorePopup.bind(this)}
                          onRemoveFromCart={this.removeFromCart.bind(this)}
                          onRequestNotificationUpdate={this.requestNotificationUpdate.bind(this)}
                          onSelectCart={this.selectCart.bind(this)}
                          onSetCurrentTrack={this.setCurrentTrack.bind(this)}
                          onSetListState={this.setListState.bind(this)}
                          onUpdateCarts={this.updateCarts.bind(this)}
                          onUpdateTracksClicked={this.updateTracks.bind(this)}
                          onAddEntityToSearch={this.addEntityToSearch.bind(this)}
                          style={{ display: !settingsVisible ? 'flex' : 'none' }}
                        />
                      </>
                    )
                  }}
                />
              </>
            )}
          </Router>
        </Root>
      </ErrorBoundary>
    )
  }
}

export default App
